import { useCallback, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
	cancelSceneChatAction,
	enqueueSceneChatAction,
	SceneChatActionEntry,
	SceneChatActionPayload,
} from 'redux/mode/chatActionQueue';
import { RootReducer } from 'redux/reducers';
import { v4 as uuid } from 'uuid';

// See useDsChatQueue for the conceptual explanation. Mirrors it for scene chats.

type EnqueueArgs = {
	label: string;
	clientKey?: string;
	payload: SceneChatActionPayload;
};

type Result = {
	items: SceneChatActionEntry[];
	enqueue: (args: EnqueueArgs) => void;
	cancel: (id: string) => void;
};

export const useSceneChatQueue = (chatId: string | null): Result => {
	const dispatch = useDispatch();

	const items = useSelector(
		(r: RootReducer) =>
			chatId ? r.mode.sceneChatActionQueue.filter((e) => e.chatId === chatId) : [],
		shallowEqual
	);

	const enqueue = useCallback(
		(args: EnqueueArgs) => {
			if (!chatId) return;
			dispatch(
				enqueueSceneChatAction({
					id: uuid(),
					chatId,
					createdAt: Date.now(),
					label: args.label,
					...(args.clientKey != null && { clientKey: args.clientKey }),
					payload: args.payload,
				})
			);
		},
		[chatId, dispatch]
	);

	const cancel = useCallback(
		(id: string) => {
			dispatch(cancelSceneChatAction({ id }));
		},
		[dispatch]
	);

	return useMemo(() => ({ items, enqueue, cancel }), [items, enqueue, cancel]);
};
